import { get } from 'lodash';

import {
  DEFAULT_NOTIFICATION_META,
  NOTIFICATION_DATA_CONTAINERS,
  NOTIFICATION_FIELD_FALLBACKS,
  NOTIFICATION_ICON_MAP,
  PRODUCT_ID_CANDIDATES,
  PRODUCT_URL_PARAM_CANDIDATES,
} from 'constants/notification';

const NOTIFICATION_ID_KEYS = ['id', 'notification_id', 'notificationId', '_id', 'uuid', 'pk', '$oid', 'value'];

function normalizeNotificationIdValue(value) {
  if (value === null || value === undefined) return null;

  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  if (typeof value === 'bigint') return value.toString();

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    return trimmedValue.length > 0 ? trimmedValue : null;
  }

  if (typeof value === 'object') {
    const candidateKey = NOTIFICATION_ID_KEYS.find(key => value[key] !== undefined && value[key] !== null);

    if (candidateKey) {
      return normalizeNotificationIdValue(value[candidateKey]);
    }
  }

  return null;
}

export function getNotificationType(notification) {
  const candidates = [
    notification?.type,
    notification?.notification_type,
    notification?.event,
    notification?.category,
    notification?.data?.type,
    notification?.data?.notification_type,
    notification?.data?.event,
    notification?.data?.category,
  ]
    .filter(Boolean)
    .map(value => String(value).toLowerCase());

  const matchedCandidate = candidates.find(candidate => {
    if (candidate.includes('tax')) return true;

    if (candidate.includes('expiry') || candidate.includes('expire')) return true;

    if (candidate.includes('stock')) return true;

    if (candidate.includes('inventory') || candidate.includes('warehouse')) return true;

    if (candidate.includes('invoice') || candidate.includes('einvoice')) return true;

    if (candidate.includes('report')) return true;

    return false;
  });

  if (matchedCandidate) {
    if (matchedCandidate.includes('tax')) return 'tax';

    if (matchedCandidate.includes('expiry') || matchedCandidate.includes('expire')) return 'expiry_lot';

    if (matchedCandidate.includes('stock')) return 'low_stock';

    if (matchedCandidate.includes('invoice') || matchedCandidate.includes('einvoice')) return 'invoice';

    if (matchedCandidate.includes('report')) return 'report';

    if (matchedCandidate.includes('warehouse') || matchedCandidate.includes('inventory')) return 'warehouse';
  }

  const message = String(notification?.message || '').toLowerCase();

  if (message.includes('tờ khai') || message.includes('nộp thuế')) return 'tax';

  if (message.includes('hết hạn')) return 'expiry_lot';

  if (message.includes('tồn kho') || message.includes('hết hàng') || message.includes('sắp hết')) return 'low_stock';

  if (message.includes('hóa đơn') || message.includes('hoá đơn') || message.includes('invoice')) return 'invoice';

  if (message.includes('báo cáo')) return 'report';

  if (message.includes('kho')) return 'warehouse';

  return null;
}

export function getNotificationId(notificationOrId) {
  if (notificationOrId === null || notificationOrId === undefined) return null;

  if (typeof notificationOrId === 'object' && !Array.isArray(notificationOrId)) {
    const candidateKey = NOTIFICATION_ID_KEYS.find(
      key => notificationOrId[key] !== undefined && notificationOrId[key] !== null
    );

    if (candidateKey) {
      return normalizeNotificationIdValue(notificationOrId[candidateKey]);
    }
  }

  return normalizeNotificationIdValue(notificationOrId);
}

export function isNotificationRead(notification) {
  if (!notification) return false;

  if (typeof notification.is_read === 'boolean') return notification.is_read;

  if (typeof notification.read === 'boolean') return notification.read;

  if (typeof notification.isRead === 'boolean') return notification.isRead;

  if (typeof notification.is_read === 'number') return notification.is_read === 1;

  if (typeof notification.read === 'number') return notification.read === 1;

  if (typeof notification.isRead === 'number') return notification.isRead === 1;

  if (typeof notification.is_read === 'string') return ['1', 'true'].includes(notification.is_read.toLowerCase());

  if (typeof notification.read === 'string') return ['1', 'true'].includes(notification.read.toLowerCase());

  if (typeof notification.isRead === 'string') return ['1', 'true'].includes(notification.isRead.toLowerCase());

  return Boolean(notification.read_at || notification.readAt);
}

export function markNotificationRead(notification) {
  return {
    ...notification,
    is_read: true,
    read: true,
    read_at: notification?.read_at || new Date().toISOString(),
  };
}

export function mergeNotificationReadState(currentNotification, incomingNotification) {
  if (!currentNotification) {
    return incomingNotification;
  }

  if (isNotificationRead(currentNotification) && !isNotificationRead(incomingNotification)) {
    return markNotificationRead({
      ...incomingNotification,
      read_at: currentNotification.read_at || currentNotification.readAt || incomingNotification.read_at,
    });
  }

  return incomingNotification;
}

export function getNotificationMeta(notification) {
  const type = getNotificationType(notification);

  return (type && NOTIFICATION_ICON_MAP[type]) || DEFAULT_NOTIFICATION_META;
}

function normalizeStringId(value) {
  if (value === null || value === undefined) return null;

  const stringValue = String(value).trim();

  return stringValue.length > 0 ? stringValue : null;
}

function getProductIdFromContainer(container) {
  if (!container || typeof container !== 'object') return null;

  const directCandidate = PRODUCT_ID_CANDIDATES.find(key => container[key] !== undefined && container[key] !== null);

  if (directCandidate) return normalizeStringId(container[directCandidate]);

  if (container.product && typeof container.product === 'object') {
    if (container.product.id !== undefined && container.product.id !== null) {
      return normalizeStringId(container.product.id);
    }
  }

  if (container.product !== undefined && container.product !== null && typeof container.product !== 'object') {
    return normalizeStringId(container.product);
  }

  if (
    container.entity &&
    String(container.entity.type || '').toLowerCase() === 'product' &&
    container.entity.id !== undefined &&
    container.entity.id !== null
  ) {
    return normalizeStringId(container.entity.id);
  }

  if (
    container.target &&
    String(container.target.type || '').toLowerCase() === 'product' &&
    container.target.id !== undefined &&
    container.target.id !== null
  ) {
    return normalizeStringId(container.target.id);
  }

  return null;
}

export function getNotificationActionUrl(notification) {
  const actionUrl = notification?.action_url || notification?.actionUrl || notification?.url || notification?.link;

  return typeof actionUrl === 'string' ? actionUrl : null;
}

export function getNotificationProductId(notification) {
  const directProductId = getProductIdFromContainer(notification);

  if (directProductId !== null) return directProductId;

  const nestedProductId = NOTIFICATION_DATA_CONTAINERS.map(containerKey =>
    getProductIdFromContainer(notification?.[containerKey])
  ).find(productId => productId !== null);

  if (nestedProductId !== undefined) return nestedProductId;

  return getNotificationProductIdFromActionUrl(getNotificationActionUrl(notification));
}

export function getNotificationProductIdFromActionUrl(actionUrl) {
  if (!actionUrl) return null;

  try {
    const parsedUrl = new URL(actionUrl, window.location.origin);
    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
    const productsIndex = pathSegments.findIndex(segment => segment.toLowerCase() === 'products');

    if (productsIndex !== -1 && pathSegments[productsIndex + 1]) {
      return normalizeStringId(pathSegments[productsIndex + 1]);
    }

    const queryValue = PRODUCT_URL_PARAM_CANDIDATES.map(param => parsedUrl.searchParams.get(param)).find(Boolean);

    if (queryValue) return normalizeStringId(queryValue);
  } catch (error) {
    const matchedProductPath = actionUrl.match(/\/products\/([^/?#]+)/i);

    if (matchedProductPath?.[1]) return normalizeStringId(matchedProductPath[1]);
  }

  return null;
}

export function normalizeBoolean(value, defaultValue = true) {
  if (value === undefined || value === null) return defaultValue;

  if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true';

  return Boolean(value);
}

export function getNotificationTypeEnabled(settingsSource, type, defaultValue = true) {
  if (Array.isArray(settingsSource)) {
    const matchedSetting = settingsSource.find(setting => setting?.notification_type === type);

    if (matchedSetting) {
      return normalizeBoolean(matchedSetting.enabled, defaultValue);
    }
  }

  const fallbackPaths = NOTIFICATION_FIELD_FALLBACKS[type] || [];
  const matchedFallback = fallbackPaths.find(path => get(settingsSource, path) !== undefined);

  if (matchedFallback) {
    return normalizeBoolean(get(settingsSource, matchedFallback), defaultValue);
  }

  return normalizeBoolean(get(settingsSource, type), defaultValue);
}

export function normalizeInternalPath(path) {
  if (!path || typeof path !== 'string') return null;

  try {
    const parsedUrl = new URL(path, window.location.origin);

    if (parsedUrl.origin !== window.location.origin) return null;

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (error) {
    if (path.startsWith('/')) return path;
  }

  return null;
}
